<!-- For input type: text, phone, email, or number -->
<template>
	<div
		:class="[
			'input',
			...variantClassList,
			hasError && 'has-error',
			hasIconErrorFeature && 'has-icon-error-feature',
			sizeIconErrorFeature && 'has-icon-error-feature',
			disabled && 'is-disabled',
			isClearFeatureAvailable && 'has-clear-feature',
			type === 'date' && 'is-date-type',
			type === 'time' && 'is-time-type'
		]"
		:disabled="disabled"
		data-testid="input"
		ref="inputField"
	>
		<Label
			class="input__label"
			:labelText="labelText"
			:forElementId="uid"
			:preventLblDisable="preventLblDisable"
			v-if="labelText"
		>
			<template v-slot:leading>
				<slot name="beforeLabel"> </slot>
			</template>

			<template v-slot:trailing>
				<slot name="afterLabel"> </slot>
			</template>
		</Label>
		<div class="input__body" :style="inputStyle" ref="popperReference">
			<div class="input__field-container" ref="inputFieldContainer">
				<input
					:class="['input__field', withFocus && 'on-clear-focus']"
					:id="uid"
					:name="name"
					ref="input"
					:type="type !== 'date' && type"
					v-model="currentValue"
					:autocomplete="autocomplete"
					:placeholder="placeholder"
					:disabled="disabled"
					:readonly="readOnly"
					@input="inputSearchText"
					@change="updateSearchText"
					@blur="blurSearchText"
					@keydown="onKeyDown"
					@keyup="onKeyUp"
					@click="onClick"
					@focus="onInputFocus"
					:aria-label="ariaLabel"
					:tabindex="tabindex"
					:style="inputElementStyle"
				/>
				<!-- DEV NOTE: please complete display implementation. Focus will always make errorVisible true -->
				<span
					class="input__error-icon"
					v-if="
						hasError &&
							(hasIconErrorFeature || sizeIconErrorFeature)
					"
					ref="errorIcon"
					@mouseover="errorVisible = true"
					@focus="errorVisible = true"
					@mouseout="errorVisible = false"
					@blur="errorVisible = false"
					tabindex="0"
				>
					<i class="icon icon-warning"></i>
				</span>
				<span
					:class="['input__clear-btn', withFocus && 'on-focus']"
					@blur="withFocus = false"
					tabindex="0"
					v-if="
						isClearFeatureAvailable &&
							type !== 'date' &&
							type !== 'time'&&
							currentValue
					"
				>
					<ButtonIcon
						iconClassName="icon-remove-2"
						tabindex="-1"
						@click="onClear"
					/>
				</span>
			</div>
			
			<ButtonIcon
				class="input__date"
				v-if="type === 'date'"
				iconClassName="icon-schedule"
				@click="$emit('buttonIconClicked')"
				v-on="$listeners"
				data-testid="date-btn"
			/>
			<ButtonIcon
				class="input__time"
				v-if="type === 'time'"
				iconClassName="icon-time"
				@click="$emit('buttonIconClicked')"
				v-on="$listeners"
				data-testid="date-btn"
			/>
		</div>
		<Tooltip
			placement="auto"
			:visible="
				errorVisible && (hasIconErrorFeature || sizeIconErrorFeature)
			"
			:trackedRefName="'popperReference'"
			positionStrategy="fixed"
			:offset="[0, 8]"
			state="negative"
		>
			<template #default>
				<ul
					class="input__icon-error-feedback"
					v-if="errorMessages.length"
				>
					<li v-for="(message, index) in errorMessages" :key="index">
						{{ message }}
					</li>
				</ul>
			</template>
		</Tooltip>
		<HelpFeedback
			class="input__help-feedback"
			:style="inputStyle"
			:helpText="helpText"
			v-if="helpText"
		/>
		<ErrorFeedback
			class="input__error-feedback"
			:errorMessages="errorMessages"
			v-if="hasError && !(hasIconErrorFeature || sizeIconErrorFeature)"
		/>
	</div>
</template>

<script>
import Label from '@/components/forms/shared-stateless-elements/label.vue';
import HelpFeedback from '@/components/forms/shared-stateless-elements/help-feedback.vue';
import ErrorFeedback from '@/components/forms/shared-stateless-elements/error-feedback.vue';
import Tooltip from '@/components/shells/tooltip/tooltip.vue';
import baseComponentMixin from '@/mixins/base-component-mixin';
import formComponentMixin from '@/mixins/form-component-mixin';
import baseInputMixin from '@/mixins/base-input-mixin';
import variantClasses from './variant-classes/input-variant-classes';
import ButtonIcon from '@/components/forms/button/button-icon.vue';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import focusMixin from '@/mixins/focus-mixin';

export default {
	name: 'Input',
	mixins: [
		baseComponentMixin,
		formComponentMixin,
		baseInputMixin,
		componentVariantMixin,
		focusMixin
	],
	data() {
		return {
			iconErrorWithFocus: false,
			isClearFeatureAvailable: false,
			sizeIconErrorFeature: false,
			errorVisible: false,
			resizeObserver: undefined,
			refField: undefined
		};
	},
	components: {
		Label,
		ButtonIcon,
		HelpFeedback,
		ErrorFeedback,
		Tooltip
	},
	created() {
		this.variantClasses = variantClasses;
	},
	mounted() {
		const input = this.$refs.inputFieldContainer;

		if (input && window.ResizeObserver !== undefined) {
			this.resizeObserver = new ResizeObserver(this.onInputResize);
			this.resizeObserver.observe(input);
		}

		this.refField = this.$refs.input;
	},
	props: {
		autocomplete: {
			type: String,
			default: 'on'
		},
		hasClearFeature: {
			type: Boolean,
			default: false
		},
		helpText: {
			type: String,
			default: null
		},
		readOnly: {
			type: Boolean,
			default: false
		},
		ariaLabel: {
			type: String,
			default: ''
		},
		width: {
			type: [String, Number],
			default: null
		},
		hasIconErrorFeature: {
			type: Boolean,
			default: false
		},
		preventLblDisable: {
			type: Boolean,
			default: false
		}
	},
	watch: {
		hasFocus: {
			immediate: true,
			deep: true,
			// eslint-disable-next-line no-unused-vars
			async handler(newVal, oldVal) {
				if (newVal) {
					await this.$nextTick();
					this.$refs.input.focus();
				}
			}
		},
		value: {
			immediate: true,
			handler(value) {
				this.currentValue = value;
			}
		}
	},
	computed: {
		hasError() {
			return this.errorMessages?.length > 0;
		},
		inputStyle() {
			if (
				typeof this.width === 'number' ||
				typeof parseInt(this.width) === 'number'
			) {
				return {
					width: `${parseInt(this.width)}px`
				};
			} else {
				return {
					width: this.width
				};
			}
		}
	},
	methods: {
		onInputResize(entries) {
			let input;

			if (entries && entries.length > 0) {
				input = entries[0].target;

				/* DEV NOTE:
				- if the input tag is small or equal 90px hasClearFeature is always false and we display the error icon and the tooltip
				- js are for testing purposes only
				- input can change the size so it's necesary to check the width on every update
				*/
				// Smart behavior
				if (input) {
					if (input.offsetWidth <= 90) {
						this.isClearFeatureAvailable = false;
						this.sizeIconErrorFeature = true;
					} else {
						this.isClearFeatureAvailable = this.hasClearFeature;
						this.sizeIconErrorFeature = false;
					}
				}
			}
		},
		onClear() {
			this.currentValue = '';
			this.$emit('input', this.currentValue);
			this.$emit('change', this.currentValue);
		},
		triggerValidation() {
			this.validate(this.currentValue, undefined);
		}
	}
};
</script>

<style lang="scss" scoped>
input[type='time']::-webkit-calendar-picker-indicator {
	background: none;
	pointer-events: none;
}
.input {
	display: block;
	font-size: $field-font-size;
	text-align: left;
	color: var(--field-fg);
	position: relative;

	&__label {
		display: flex;
		margin-bottom: $gutter-sm;
	}

	&__body {
		position: relative;
	}

	&__error-icon {
		align-items: center;
		color: var(--ui-negative);
		padding: $field-padding-y $field-padding-x;
	}

	&__clear-btn {
		background: var(--bg-0);
		display: inline-flex;
		border-radius: 0 $border-radius $border-radius 0;
		&:hover {
			background-color: var(--bg-brand-2-tint);
			color: var(--bg-brand-2-tint-fg-2);
		}

		&.on-focus {
			display: inline-flex;
		}
	}

	&__error-icon,
	&__clear-btn {
		height: 100%;
		&:focus,
		&:active {
			outline: $focus-width $focus-style var(--brand-2);
			outline-offset: $focus-offset;
		}
	}

	&:deep(.btn-icon) {
		border-radius: 0;
		padding: $field-padding-y $field-padding-x;

		&:hover {
			.icon:after {
				border: none;
			}
		}
		&:focus,
		&:active {
			.icon:after {
				border: none;
			}
		}
	}

	&__field-container {
		@include input-height;
		position: relative;
		display: flex;
		align-items: center;
		border-radius: $border-radius;
		border: $field-border;
		&::after {
			content: none;
			@include transform-vertical-center;
			left: -1px;
			transform: translateY(-50%);
			width: calc(100% + $focus-width);
			height: calc(100% + $focus-width);
			border-radius: $border-radius;
			border: $focus-width $focus-style var(--brand-2);
			pointer-events: none;
			z-index: $z-level1-1;
			
		}

		&:focus-within {
			&::after {
				content: '';
				z-index: $z-level1-1;
			}

			.input__field {
				border-color: transparent;
			}

			& .input__clear-btn {
				display: inline-flex;
				
			}
		}
	}

	&__field {
		width: 100%;
		height: 100%;
		border: 0;
		border-radius: $border-radius;
		padding: $field-padding-y $field-padding-x;
		background: var(--field-bg);
		color: var(--field-fg);
		
		@include ellipsis;

		&:focus,
		&:active {
			outline: none;
		}
	
	}
	
	&.is-date-type,
	&.is-time-type {
		.input {
			&__body {
				display: flex;
				align-items: center;
				justify-content: space-between;
			}

			&__field-container {
				flex-grow: 2;
				&::after {
					width: calc(100% + 28px);
				}
			}

			&__field {
				border-top-right-radius: 0;
				border-bottom-right-radius: 0;
				padding-right: 0;
			}
		}
	}

	&__date,
	&__time {
		@include input-height;
		@include btn-clear;
		min-width: auto;
		color: inherit;
		border: $field-border;
		border-left: none;

		@include mq($from: lg) {
			min-width: auto;
		}

		&.btn-icon {
			border-top-right-radius: $border-radius;
			border-bottom-right-radius: $border-radius;
			margin-left: -1px;
			padding: $field-padding-y + 2px $field-padding-x;
			font-size: 12px;
		}
	}

	&.has-clear-feature {
		.input__field:focus,
		.input__field.on-clear-focus {
			padding-right: 0;
		}
	}

	&.has-error {
		.input__field-container::after {
			content: '';
			border-color: var(--ui-negative);
		}

		.input__field {
			border-color: transparent;
		}

		.input__error-icon {
			display: inline-flex;
		}

		&.has-icon-error-feature {
			.input__field {
				padding-right: 0;
			}
		}
	}
}
@include left-label('input');
</style>
